import { Box, Center, Spinner, Text } from '@chakra-ui/react'
import { Navigate, useParams } from 'react-router'
import { Paths } from 'src/constants/paths'
import { useCurrentMember } from 'src/features/auth/context'
import { usePatient } from 'src/features/patient/api'

import { UID } from '../../../../utils/brandedUid'
import { useWorksheet } from '../../api'
import { PatientInfo, Worksheet } from '../../types'
import { canPrintWorksheet } from '../../utils/permissions'
import { WorksheetPrintView } from '../WorksheetPrintView/WorksheetPrintView'

type Param = {
  worksheetUid: Worksheet['uid']
}

export const WorksheetPrintScreen: React.FC = () => {
  const { worksheetUid = '' as UID<'Worksheet'> } = useParams<Param>()

  const { data: worksheet, isLoading: isWorksheetLoading } = useWorksheet({
    worksheetUid,
    revalidateIfStale: false,
    revalidateOnFocus: false,
  })

  const { data: patientData, isLoading: isPatientLoading } = usePatient({
    patientUid: worksheet?.patientUid ?? null,
    revalidateIfStale: false,
    revalidateOnFocus: false,
  })

  const { currentMember } = useCurrentMember()

  const isLoading = isWorksheetLoading || isPatientLoading

  // ローディング中の表示
  if (isLoading || !worksheet) {
    return (
      <Center h="100vh">
        <Box textAlign="center">
          <Spinner size="xl" color="blue.500" />
          <Text mt="4" color="gray.600">
            読み込み中...
          </Text>
        </Box>
      </Center>
    )
  }

  // 権限チェック: Dr・CRCのみ、保存済みのみ
  if (!canPrintWorksheet(currentMember.role, worksheet.status)) {
    return <Navigate to={Paths.Http403} replace />
  }

  const patient: PatientInfo | undefined = patientData
    ? {
        diseaseUid: patientData.diseaseUid,
        lastName: patientData.lastName ?? undefined,
        firstName: patientData.firstName ?? undefined,
        birthday: patientData.birthday,
      }
    : undefined

  return <WorksheetPrintView worksheet={worksheet} patient={patient} />
}
